/* eslint-disable jsx-a11y/mouse-events-have-key-events */
'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { ChevronRightIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { noop } from '@/lib/constants';
import { AnimatedLink } from '@/components/AnimatedLink';
import { SubMenuItemType } from './data';
import { navigationBarItems, subMenuItems } from './data';
import { BuildScanBanner } from './BuildScanBanner';
import { BuildScanButton } from './BuildScanButton';

type SubNavButtonProps = {
  item: SubMenuItemType;
  handleSubNavClick: (menuOpenIdx: number, idx: number) => void;
  menuOpenIdx: number;
  idx: number;
  getCurrentSubMenuOpenIdx: (menuOpenIdx: number) => number;
};

const SubNavButton = ({ item, handleSubNavClick, menuOpenIdx, idx, getCurrentSubMenuOpenIdx }: SubNavButtonProps) => {
  const buttonContent = (
    <>
      <div className="flex items-center gap-2">
        {item.logo ? (
          <div className="py-2.5">{item.logo}</div>
        ) : (
          <>
            {item.icon && (
              <item.icon
                className={clsx('size-6 group-hover:text-gradle-blue group-active:text-gradle-blue', {
                  'text-gradle-blue': idx === getCurrentSubMenuOpenIdx(menuOpenIdx),
                })}
              />
            )}
            <span
              className={clsx('group-hover:gradle-gradient-text text-body', {
                'gradle-gradient-text': idx === getCurrentSubMenuOpenIdx(menuOpenIdx),
              })}
            >
              {item.buttonContent}
            </span>
          </>
        )}
      </div>
      {item?.link ? null : <ChevronRightIcon className="size-5 transition-transform group-hover:translate-x-1" />}
      {item?.buttonContent === 'Develocity' && (
        <ChevronRightIcon className="size-5 transition-transform group-hover:translate-x-1" />
      )}
    </>
  );

  if (item.link) {
    return (
      <div className="flex flex-col gap-2">
        <Link
          href={item.link.href}
          target={item.link.external ? '_blank' : undefined}
          onClick={item.link ? noop : () => handleSubNavClick(menuOpenIdx, idx)}
          className={clsx(
            'mega-menu-link group flex w-full items-center justify-between rounded-lg px-4 py-2 hover:bg-dark-background',
            {
              'bg-dark-background': idx === getCurrentSubMenuOpenIdx(menuOpenIdx),
            },
          )}
          key={item.buttonContent}
        >
          {buttonContent}
        </Link>
        {item.buttonContent === 'Develocity' && <BuildScanButton className="mega-menu-link" />}
      </div>
    );
  }

  return (
    <button
      onClick={item.link ? noop : () => handleSubNavClick(menuOpenIdx, idx)}
      className={clsx('group flex w-full items-center justify-between rounded-lg px-4 py-2 hover:bg-dark-background', {
        'bg-dark-background': idx === getCurrentSubMenuOpenIdx(menuOpenIdx),
      })}
      key={item.buttonContent}
    >
      {buttonContent}
    </button>
  );
};

type DesktopNavbarProps = {
  menuOpenIdx: number | null;
  handleMouseOutCloseMenu: () => void;
  setMenuOpenIdx: (idx: number | null) => void;
};

const DesktopNavbarDrawer = ({ menuOpenIdx, setMenuOpenIdx, handleMouseOutCloseMenu }: DesktopNavbarProps) => {
  //    For each nav item that opens the nav drawer on hover, keep track
  //    of the index of the sub menu item that is currently open.
  const [subMenuTabOpenArray, setSubMenuTabOpenArray] = useState([0, 0, 0, 0]);

  const handleSubNavClick = (menuOpenIdx: number, idx: number) => {
    const currentOpenNav = navigationBarItems[menuOpenIdx].leftNavSection.title;

    //   Keep track of which tab is open depending
    //   on which nav item is currently open
    if (currentOpenNav === 'Products') {
      setSubMenuTabOpenArray([idx, 0, 0, 0]);
    } else if (currentOpenNav === 'Solutions') {
      setSubMenuTabOpenArray([0, idx, 0, 0]);
    } else if (currentOpenNav === 'Resources') {
      setSubMenuTabOpenArray([0, 0, idx, 0]);
    } else if (currentOpenNav === 'Company') {
      setSubMenuTabOpenArray([0, 0, 0, idx]);
    }
  };

  const getCurrentSubMenuOpenIdx = (menuOpenIdx: number) => {
    const currentOpenNav = navigationBarItems[menuOpenIdx].leftNavSection.title;

    if (currentOpenNav === 'Products') {
      return subMenuTabOpenArray[0];
    } else if (currentOpenNav === 'Solutions') {
      return subMenuTabOpenArray[1];
    } else if (currentOpenNav === 'Resources') {
      return subMenuTabOpenArray[2];
    } else if (currentOpenNav === 'Company') {
      return subMenuTabOpenArray[3];
    }

    return 0;
  };

  useEffect(() => {
    //  Reset the sub menu tab open array when the nav drawer is closed per design
    setSubMenuTabOpenArray([0, 0, 0, 0]);

    if (menuOpenIdx != null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [menuOpenIdx]);

  const isProductsMenuOpen = menuOpenIdx === 0;

  return (
    <>
      <div
        className={clsx(
          'absolute left-0 z-10 hidden  w-full overflow-hidden bg-[#161617] px-10 transition-all lg:block',
          isProductsMenuOpen
            ? 'h-[27rem] max-h-[27rem]'
            : menuOpenIdx != null
              ? 'visible h-[22rem] max-h-[22rem]'
              : 'max-h-0',
        )}
      >
        {menuOpenIdx != null && (
          <div className="max-w-section flex h-full py-10">
            {/* Left-side Subnav menu section */}
            <div className="flex min-w-[17rem] flex-1 flex-col gap-6 pr-6">
              <div className="text-caption text-gray-3">{navigationBarItems[menuOpenIdx].leftNavSection.title}</div>
              <div className="flex flex-col gap-4">
                {navigationBarItems[menuOpenIdx].leftNavSection?.subMenuItems?.map((item, idx) => (
                  <SubNavButton
                    key={item.buttonContent}
                    item={item}
                    idx={idx}
                    handleSubNavClick={handleSubNavClick}
                    menuOpenIdx={menuOpenIdx}
                    getCurrentSubMenuOpenIdx={getCurrentSubMenuOpenIdx}
                  />
                ))}
              </div>
            </div>
            {/* Right-side nav items */}
            <div className="flex flex-[4] flex-col justify-between border-l-[1px] border-dark-background pl-6">
              <div className="flex flex-col gap-4">
                <div className="text-caption text-gray-3">{navigationBarItems[menuOpenIdx].rightNavSectionTitle}</div>
                <div className={clsx('flex h-full', isProductsMenuOpen ? 'justify-between' : 'gap-20')}>
                  {subMenuItems[
                    navigationBarItems[menuOpenIdx].leftNavSection.subMenuItems[getCurrentSubMenuOpenIdx(menuOpenIdx)]
                      ?.menuItemKey ?? 'product'
                  ]?.map((item, idx) => (
                    <div
                      key={`nav-${idx}`}
                      className={clsx(
                        'flex h-full gap-2',
                        item.type !== 'links' && 'w-full',
                        isProductsMenuOpen && 'flex-1',
                      )}
                    >
                      {item.icon && <item.icon className="size-6 text-gradle-blue" />}
                      {item.type === 'links' && (
                        <div className="flex grow-0 flex-col gap-6">
                          {item.title && (
                            <Link
                              href={item.link?.href ?? '/'}
                              className="mega-menu-link text-body-bold underline-offset-4 transition-all hover:text-gradle-blue hover:underline"
                            >
                              {item.title}
                            </Link>
                          )}
                          <ul className="flex flex-col gap-4 text-gray-4">
                            {item.items.map(subItem => (
                              <Link
                                href={subItem.link.href}
                                target={subItem.link.external ? '_blank' : undefined}
                                key={subItem.title}
                                className="mega-menu-link text-footnote group flex items-center gap-1"
                                onClick={() => {
                                  if (subItem.link.href?.includes('#')) {
                                    setMenuOpenIdx(null);
                                  }
                                }}
                              >
                                {subItem.icon && (
                                  <subItem.icon className="size-4 text-gray-4 group-hover:text-light-blue" />
                                )}
                                {subItem.reactIcon && subItem.reactIcon}
                                <li className="flex h-5 items-center group-hover:text-gray-5">{subItem.title}</li>
                                <ArrowRightIcon className="ml-2 size-3.5 text-light-blue opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                              </Link>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.type === 'cards' && (
                        <>
                          {/* These are the Learning Center Cards */}
                          {item.items.map(subItem => (
                            <Link
                              href={subItem.link.href}
                              key={subItem.title}
                              className="mega-menu-link group relative flex h-48 w-full flex-col justify-between rounded-lg border border-dark-background p-6"
                            >
                              <div className="flex flex-col">
                                <h5 className="text-body-bold z-10">{subItem.title}</h5>
                                <p className="text-input-label z-10 max-w-52 text-gray-4">{subItem.text}</p>
                              </div>
                              {subItem.link.text && <AnimatedLink className="z-10" linkText={subItem.link.text} />}

                              {subItem.bgImg && (
                                <Image alt="decorative" className="rounded-lg object-cover" src={subItem.bgImg} fill />
                              )}
                            </Link>
                          ))}
                        </>
                      )}
                      {item.type === 'mini-cards' && (
                        <div className="flex w-full flex-col gap-4">
                          <div className="flex flex-col gap-2">
                            <h6 className="text-caption text-gray-3">Open Source Projects</h6>
                            <p className="text-input-label text-gray-3 ">
                              Gradle proudly supports the world&apos;s most used OSS technologies with free Develocity
                              licenses.
                            </p>
                          </div>
                          {/* These are the OSS mini cards */}
                          <div className="grid grid-cols-3 gap-4">
                            {item.items.map(subItem => (
                              <Link
                                href={subItem.link.href}
                                key={subItem.title}
                                className={clsx(
                                  'mega-menu-link group relative flex h-[5.5rem] w-full flex-col justify-center rounded-2xl border border-[#2b2b2b] p-6 shadow-sm transition-colors hover:border-gray-2',
                                  subItem.reactIcon ? 'bg-[#13171B]' : 'bg-gray-1',
                                )}
                              >
                                <div className={clsx('flex max-w-28', subItem.reactIcon && 'h-9')}>
                                  <h5
                                    className={clsx(
                                      'text-input-label z-10',
                                      subItem.reactIcon ?? 'underline underline-offset-4',
                                    )}
                                  >
                                    {subItem.title}
                                  </h5>
                                </div>
                                {subItem.reactIcon && subItem.reactIcon}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {isProductsMenuOpen && <BuildScanBanner />}
            </div>
          </div>
        )}
      </div>
      <div
        onMouseOver={handleMouseOutCloseMenu}
        className={clsx(
          'fixed inset-0 top-[calc(var(--navbarHeight)+var(--navbarBannerHeight))] hidden w-full overflow-hidden bg-[#1616173d] px-10 backdrop-blur-lg lg:block',
          menuOpenIdx != null ? 'visible' : 'invisible',
        )}
      ></div>
    </>
  );
};
export default DesktopNavbarDrawer;
